// src/pages/AddListing/styles.js
import { COLORS, LAYOUT, TYPOGRAPHY } from '../../constants';

export const addListingStyles = {
  // Page Container - Full width with proper spacing
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: COLORS.grayLight,
    padding: `${LAYOUT.spacing.xxxl} ${LAYOUT.spacing.lg}`,
  },

  // Content Wrapper - Centered with max width
  contentWrapper: {
    maxWidth: '900px',
    margin: '0 auto',
  },

  // Header Section
  header: {
    textAlign: 'center',
    marginBottom: LAYOUT.spacing.xxxl,
  },

  pageTitle: {
    ...TYPOGRAPHY.heading1,
    color: COLORS.primary,
    fontSize: '2.5rem',
    fontWeight: '700',
    marginBottom: LAYOUT.spacing.md,
  },

  pageSubtitle: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '1.125rem',
    marginBottom: 0,
  },

  // Form
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xl,
  },

  // Form Card - Elevated sections
  formCard: {
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xl,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${COLORS.border}`,
  },

  cardTitle: {
    ...TYPOGRAPHY.heading3,
    color: COLORS.text.primary,
    fontSize: '1.5rem',
    fontWeight: '600',
    marginBottom: LAYOUT.spacing.xl,
    paddingBottom: LAYOUT.spacing.md,
    borderBottom: `2px solid ${COLORS.grayLight}`,
  },

  // Input Group
  inputGroup: {
    marginBottom: LAYOUT.spacing.lg,
  },

  label: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    marginBottom: LAYOUT.spacing.sm,
    fontSize: '0.9375rem',
    fontWeight: '500',
    color: COLORS.text.primary,
  },

  // Input Row - For side by side inputs
  inputRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: LAYOUT.spacing.lg,
  },

  // Input Styles
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '1rem',
    borderRadius: LAYOUT.borderRadius.md,
    border: `2px solid ${COLORS.border}`,
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    backgroundColor: COLORS.white,
    color: COLORS.text.primary,
    ':focus': {
      borderColor: COLORS.primary,
      boxShadow: `0 0 0 3px ${COLORS.hover}`,
    },
  },

  textarea: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '1rem',
    borderRadius: LAYOUT.borderRadius.md,
    border: `2px solid ${COLORS.border}`,
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    backgroundColor: COLORS.white,
    color: COLORS.text.primary,
    minHeight: '140px',
    resize: 'vertical',
    lineHeight: '1.5',
    ':focus': {
      borderColor: COLORS.primary,
      boxShadow: `0 0 0 3px ${COLORS.hover}`,
    },
  },

  // Image Upload Section
  uploadSection: {
    width: '100%',
  },

  hiddenFileInput: {
    display: 'none',
  },

  dropZone: {
    border: `2px dashed ${COLORS.border}`,
    borderRadius: LAYOUT.borderRadius.lg,
    padding: LAYOUT.spacing.xxxl,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: COLORS.white,
    ':hover': {
      borderColor: COLORS.primary,
      backgroundColor: COLORS.hover,
    },
  },

  dropZoneTitle: {
    ...TYPOGRAPHY.heading4,
    color: COLORS.text.primary,
    fontSize: '1.25rem',
    fontWeight: '600',
    marginTop: LAYOUT.spacing.md,
    marginBottom: LAYOUT.spacing.xs,
  },

  dropZoneText: {
    color: COLORS.text.secondary,
    fontSize: '1rem',
    marginBottom: LAYOUT.spacing.sm,
  },

  dropZoneHint: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.xs,
    color: COLORS.text.muted,
    fontSize: '0.875rem',
  },

  // Preview Section
  previewSection: {
    marginTop: LAYOUT.spacing.xl,
  },

  previewHeader: {
    marginBottom: LAYOUT.spacing.md,
  },

  previewCount: {
    fontSize: '0.9375rem',
    fontWeight: '500',
    color: COLORS.text.secondary,
  },

  previewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: LAYOUT.spacing.md,
  },

  previewCard: {
    position: 'relative',
    aspectRatio: '1',
    borderRadius: LAYOUT.borderRadius.md,
    overflow: 'hidden',
    backgroundColor: COLORS.grayLight,
    border: `2px solid ${COLORS.border}`,
    transition: 'all 0.2s ease',
    ':hover': {
      transform: 'scale(1.02)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
  },

  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  removeButton: {
    position: 'absolute',
    top: LAYOUT.spacing.sm,
    right: LAYOUT.spacing.sm,
    width: '32px',
    height: '32px',
    borderRadius: LAYOUT.borderRadius.full,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: COLORS.white,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(4px)',
  },

  primaryBadge: {
    position: 'absolute',
    bottom: LAYOUT.spacing.sm,
    left: LAYOUT.spacing.sm,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    padding: '4px 10px',
    borderRadius: LAYOUT.borderRadius.sm,
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Submit Button
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.sm,
    width: '100%',
    maxWidth: '400px',
    margin: `${LAYOUT.spacing.lg} auto 0`,
    padding: '16px 32px',
    fontSize: '1.125rem',
    fontWeight: '600',
    color: COLORS.white,
    backgroundColor: COLORS.primary,
    border: 'none',
    borderRadius: LAYOUT.borderRadius.md,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 4px 12px ${COLORS.shadow}`,
    ':hover': {
      backgroundColor: COLORS.primaryDark,
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 20px ${COLORS.shadow}`,
    },
  },

  submitButtonDisabled: {
    backgroundColor: COLORS.gray,
    cursor: 'not-allowed',
    opacity: 0.6,
    ':hover': {
      transform: 'none',
      boxShadow: `0 4px 12px ${COLORS.shadow}`,
    },
  },

  // Error Message
  errorMessage: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: LAYOUT.spacing.sm,
    padding: LAYOUT.spacing.md,
    marginBottom: LAYOUT.spacing.xl,
    backgroundColor: '#FFF3F3',
    border: `2px solid ${COLORS.primary}`,
    borderRadius: LAYOUT.borderRadius.md,
    color: COLORS.primary,
    fontSize: '0.9375rem',
    fontWeight: '500',
  },
};